import {
  DocumentRequest,
  DocumentResponse,
  FCMTokenRequest,
  FCMTokenResponse,
  OrgsDocumentRequest,
  OrgsDocumentResponse,
  RecordEngagementRequest,
  RecordEngagementResponse,
  ResendOTPRequest,
  ResendOTPResponse,
  SendOTPRequest,
  SendOTPResponse,
  UserData,
  UserPermissionsResponse,
  UserTokens,
  ValidateGstInRequest,
  ValidateGstInResponse,
  VerifyOTPRequest,
  Workspace,
} from './userAuth.interface';
import { zonoApi } from '../api/zonoApi';
import {
  getWorkspaceId,
  getUserAuthId,
  logOut,
  updateLogInTracked,
  updateUserRoles,
  OnGuestUserAuthenticated,
  authenticate,
  onAuthSuccess,
  onSendOTPSuccess,
  onResendOTPSuccess,
  onRefreshToken,
  updateUserPermissions,
  updateUser,
} from './userAuth.slice';
import { AppName, DevicePlatform } from '@zonofi/common/types/enum';
import { RootState } from '../root/root-types';
import { APP_TYPE } from '../app';

export const userApi = zonoApi.injectEndpoints({
  endpoints: (build) => ({
    sendOTP: build.mutation<SendOTPResponse, SendOTPRequest>({
      query: (SendOTPRequest) => {
        return {
          url: 'sendotp',
          body: SendOTPRequest,
          method: 'POST',
        };
      },

      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data?.temptoken) {
            dispatch(OnGuestUserAuthenticated(data.temptoken));
          }
          dispatch(onSendOTPSuccess(data));
        } catch (error) {
          console.error('Error during sendOTP:', error);
        }
      },
    }),
    resendOTP: build.mutation<ResendOTPResponse, ResendOTPRequest>({
      query: (ResendOTPRequest) => {
        return {
          url: 'resend-otp',
          body: ResendOTPRequest,
          method: 'POST',
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((res) => dispatch(onResendOTPSuccess(res.data)))
          .catch((e) => console.error('resendOTP', e));
      },
    }),
    verifyOTP: build.mutation<UserData, VerifyOTPRequest>({
      query: (VerifyOTPRequest) => {
        return {
          url: 'verifyotp',
          body: VerifyOTPRequest,
          method: 'POST',
        };
      },

      async onQueryStarted(request, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;

          if (
            !(data.attemptsLeft === 0 || data?.alertMessage || data?.message)
          ) {
            dispatch(authenticate());
            dispatch(onAuthSuccess(data));
            if ((getState() as RootState).app === APP_TYPE.SELLER_HUB) {
              if (!localStorage.getItem('isTeamMember')) {
                let sevenDays = Date.now() + 7 * 24 * 60 * 60 * 1000;
                localStorage.setItem('isTeamMember', JSON.stringify(sevenDays));
              }
            }
          }
        } catch (error) {
          console.error('Error during verifyOTP:', error);
        }
      },
    }),
    getUser: build.query<UserData, any>({
      query: (args) => {
        return {
          url: 'users/me',
          method: 'GET',
          params: args,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((res) => dispatch(updateUser(res.data)))
          .catch((e) => console.log('getUser', e));
      },
    }),
    regenerateToken: build.mutation<
      UserTokens,
      { refreshToken: string; token: string; workspaceId?: string }
    >({
      query(arg) {
        return {
          url: 'regenerate/token',
          body: arg,
          method: 'POST',
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            response?.data?.workspaceRoles &&
              dispatch(updateUserRoles(response?.data?.workspaceRoles));
            dispatch(onRefreshToken(response.data));
          })
          .catch((x) => {
            console.error(x);
          });
      },
    }),
    validateKycGst: build.query<ValidateGstInResponse, ValidateGstInRequest>({
      query(payload: ValidateGstInRequest) {
        const { gstNumber, sellerWorkspaceId } = payload;
        return {
          url: `/hub/orgs/api/validate/gst/${gstNumber}?sellerWorkspaceId=${sellerWorkspaceId}`,
          method: 'GET',
          params: {
            skipWorkspaceCheck: true,
            customerId: payload.customerId,
          },
        };
      },
    }),
    getDocuments: build.query<DocumentResponse[], DocumentRequest>({
      query(arg) {
        const { workspaceId, ...params } = arg;
        return {
          url: `hub/orgs/api/workspace/documents`,
          method: 'GET',
          params: { ...params, sellerWorkspaceId: workspaceId },
        };
      },
    }),

    recordEngagement: build.mutation<
      RecordEngagementResponse,
      RecordEngagementRequest
    >({
      query: (RecordEngagementRequest) => ({
        url: `/hub/communication-v2/api/communicate/engagement`,
        method: 'POST',
        body: RecordEngagementRequest,
      }),
    }),
    getWorkspace: build.query<Workspace[], void>({
      query() {
        return {
          url: `workspaces`,
          method: 'GET',
        };
      },
    }),
    getUserPermissions: build.query<UserPermissionsResponse[], void>({
      query: () => {
        return {
          url: 'workspaces/roles/details',
          method: 'GET',
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        dispatch(
          userApi.endpoints.trackEvent.initiate({
            eventType: 'log_in',
          })
        );
        queryFulfilled
          .then((res) => dispatch(updateUserPermissions(res.data)))
          .catch((e) => console.error('getUserPermissions', e));
      },
    }),
    trackEvent: build.mutation<void, { eventType: 'log_in' | 'log_out' }>({
      query(arg) {
        return {
          url: `track`,
          method: 'POST',
          body: arg,
          keepalive: true,
        };
      },
      async onQueryStarted(arg, { dispatch }) {
        if (arg.eventType === 'log_in') {
          dispatch(updateLogInTracked(true));
        }
      },
    }),
    deleteDocument: build.query<DocumentResponse[], DocumentRequest>({
      query(arg) {
        const { workspaceId, ...params } = arg;
        return {
          url: `workspaces/documents/delete/${workspaceId}`,
          method: 'POST',
          params: params,
        };
      },
    }),
    getWorkspaceDocument: build.query<
      OrgsDocumentResponse[],
      OrgsDocumentRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/docs/presignedurl/${arg.sellerWorkspaceId}`,
          method: 'GET',
          params: arg,
        };
      },
    }),
    updateFCMToken: build.mutation<FCMTokenResponse, FCMTokenRequest>({
      query: (args) => {
        return {
          url: `/hub/communication-v2/api/fcm`,
          body: args,
          method: 'POST',
        };
      },
    }),
    logout: build.mutation<string, void>({
      query() {
        return {
          url: 'logout',
          method: 'PUT',
          responseHandler(response) {
            return response.text();
          },
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const state = getState() as RootState;
        dispatch(
          userApi.endpoints.trackEvent.initiate({
            eventType: 'log_out',
          })
        );
        dispatch(
          userApi.endpoints.updateFCMToken.initiate({
            appName: AppName.SELLER,
            devicePlatform: DevicePlatform.WEB,
            token: '',
            userId: getUserAuthId(state),
            sellerWorkspaceId: getWorkspaceId(state),
          })
        );
        queryFulfilled.then(() => {
          dispatch(logOut());
        });
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyValidateKycGstQuery,
  useLazyGetDocumentsQuery,
  useRecordEngagementMutation,
  useLazyGetWorkspaceQuery,
  useLazyGetUserPermissionsQuery,
  useTrackEventMutation,
  useLazyGetUserQuery,
  useRegenerateTokenMutation,
  useSendOTPMutation,
  useResendOTPMutation,
  useVerifyOTPMutation,
  useLazyDeleteDocumentQuery,
  useUpdateFCMTokenMutation,
  useLogoutMutation,
  useLazyGetWorkspaceDocumentQuery,
} = userApi;
